import { motion } from 'framer-motion';
import { Lock, CheckCircle, CreditCard, MessageCircle } from 'lucide-react';
import { CountdownTimer } from '@/components/CountdownTimer';
import { FillProgressBar } from '@/components/FillProgressBar';
import { getCountdownState, useServerTime } from '@/lib/countdown-utils';
import { getAvatarPath } from '@/lib/avatar-utils';
import { InviteShareButton } from '@/components/InviteShareButton';
import { BRACKET_OPEN_DATE, BRACKET_DEADLINE } from '@/types/bracket';

export interface WaitingRoomMember {
  user_id: string;
  display_name: string;
  avatar_id: number | null;
  seriesBetCount: number;
  sideBetPickCount: number;
}

interface WaitingRoomProps {
  members: WaitingRoomMember[];
  groupId: string;
  totalActiveSeries?: number;
  totalOpenSideBets?: number;
  isCommissioner?: boolean;
  inviteCode?: string;
  groupName?: string;
  inviteRestricted?: boolean;
  paymentLink?: string | null;
  chatLink?: string | null;
}

const containerVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, staggerChildren: 0.05 },
  },
};

const avatarVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.25 } },
};

export function WaitingRoom({ members, groupId, totalActiveSeries = 0, totalOpenSideBets = 0, isCommissioner = false, inviteCode, groupName, inviteRestricted = false, paymentLink, chatLink }: WaitingRoomProps) {
  const { now: serverNow, ready } = useServerTime();

  // During SSR / initial load, derive state from client time to avoid flicker
  const currentTime = ready ? serverNow() : Date.now();
  const countdownState = getCountdownState(currentTime);

  const totalCount = members.length;

  // Combined bet stats: series bets + side bet picks
  const totalBets = totalActiveSeries + totalOpenSideBets;
  const filledFor = (m: WaitingRoomMember) => m.seriesBetCount + m.sideBetPickCount;
  const allBettedCount = members.filter((m) => filledFor(m) >= totalBets && totalBets > 0).length;

  // Sort: members who haven't completed all bets first, then by filled count ascending, then alphabetical
  const sortedMembers = [...members].sort((a, b) => {
    const aFilled = filledFor(a);
    const bFilled = filledFor(b);
    const aComplete = aFilled >= totalBets && totalBets > 0 ? 1 : 0;
    const bComplete = bFilled >= totalBets && totalBets > 0 ? 1 : 0;
    if (aComplete !== bComplete) return aComplete - bComplete;
    if (aFilled !== bFilled) return aFilled - bFilled;
    return a.display_name.localeCompare(b.display_name, 'he');
  });

  // ── Locked state ──────────────────────────────────────────────────────────────
  if (countdownState === 'locked') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-[hsl(0,0%,8%)] rounded-2xl border border-border p-5"
        dir="rtl"
      >
        <div className="flex items-center gap-3">
          <Lock className="w-5 h-5 text-muted-foreground shrink-0" />
          <div>
            <p className="text-lg font-bold tracking-tight">הבראקט ננעל</p>
            <p className="text-sm text-muted-foreground">הפלייאוף התחיל!</p>
          </div>
          <CheckCircle className="w-5 h-5 text-green-500 ml-auto shrink-0" />
        </div>
      </motion.div>
    );
  }

  // ── Bracket-open state ────────────────────────────────────────────────────────
  if (countdownState === 'bracket-open') {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="bg-[hsl(0,0%,8%)] rounded-2xl border border-border p-5 space-y-4"
        dir="rtl"
      >
        {/* Header */}
        <div className="flex flex-col gap-1">
          <p className="text-lg font-bold text-orange-400 tracking-tight">הפלייאוף מתחיל היום!</p>
          <p className="text-sm text-white/65">משחק ראשון ב-20:00</p>
        </div>

        {/* PRIMARY: Combined bets progress bar (series + side bets) */}
        {totalBets > 0 && (
          <FillProgressBar
            filled={allBettedCount}
            total={totalCount}
            deadlineMs={new Date(BRACKET_DEADLINE).getTime()}
            currentTimeMs={currentTime}
            label={`השלימו את כל ${totalBets} ההימורים`}
          />
        )}

        {/* Avatar grid with combined bet status */}
        <div className="flex flex-wrap gap-3">
          {sortedMembers.map((member) => {
            const memberFilled = filledFor(member);
            const allBetted = memberFilled >= totalBets && totalBets > 0;
            return (
              <motion.div
                key={member.user_id}
                variants={avatarVariants}
                className={`flex flex-col items-center gap-1 ${!allBetted ? 'opacity-60' : ''}`}
              >
                <div className="relative">
                  <img
                    src={getAvatarPath(member.avatar_id ?? 1)}
                    alt={member.display_name}
                    className={`w-12 h-12 rounded-full object-cover border border-border ${allBetted ? 'ring-2 ring-green-500' : ''}`}
                  />
                  <span className="absolute -bottom-0.5 -right-0.5 text-xs leading-none">
                    {allBetted ? '✅' : '⏳'}
                  </span>
                </div>
                <span className="text-[11px] text-muted-foreground max-w-[72px] truncate text-center">
                  {member.display_name}
                </span>
                {/* Combined bet count: series + side bets */}
                <span className={`text-[10px] leading-tight ${allBetted ? 'text-green-400' : 'text-orange-400'}`}>
                  {memberFilled}/{totalBets} הימורים
                </span>
              </motion.div>
            );
          })}
        </div>

        {/* Commissioner payment + chat links */}
        <CommissionerLinks paymentLink={paymentLink} chatLink={chatLink} />

        {/* Invite share button (compact in bracket-open) */}
        {inviteCode && groupName && (isCommissioner || !inviteRestricted) && (
          <InviteShareButton inviteCode={inviteCode} groupName={groupName} variant="prominent" />
        )}
      </motion.div>
    );
  }

  // ── Pre-bracket state ─────────────────────────────────────────────────────────
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="bg-[hsl(0,0%,8%)] rounded-2xl border border-border p-5 space-y-4"
      dir="rtl"
    >
      {/* Header */}
      <div className="flex flex-col gap-1">
        <p className="text-lg font-bold tracking-tight">הקבוצה מתארגנת</p>
        <p className="text-sm text-muted-foreground">הפלייאוף מתחיל בקרוב</p>
        <CountdownTimer targetDate={BRACKET_OPEN_DATE} mode="compact" label="נפתח בעוד" />
      </div>

      {/* Avatar grid */}
      <div className="flex flex-wrap gap-3">
        {members.map((member) => (
          <motion.div
            key={member.user_id}
            variants={avatarVariants}
            className="flex flex-col items-center gap-1"
          >
            <img
              src={getAvatarPath(member.avatar_id ?? 1)}
              alt={member.display_name}
              className="w-12 h-12 rounded-full object-cover border border-border"
            />
            <span className="text-[11px] text-muted-foreground max-w-[72px] truncate text-center">
              {member.display_name}
            </span>
          </motion.div>
        ))}
      </div>

      {/* Member count */}
      <p className="text-sm text-muted-foreground">{totalCount} חברים בקבוצה</p>

      {/* Commissioner payment + chat links */}
      <CommissionerLinks paymentLink={paymentLink} chatLink={chatLink} />

      {/* Invite share button (prominent in pre-bracket) */}
      {inviteCode && groupName && (isCommissioner || !inviteRestricted) && (
        <InviteShareButton inviteCode={inviteCode} groupName={groupName} variant="prominent" />
      )}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// CommissionerLinks — payment + group chat pills
// ---------------------------------------------------------------------------

function CommissionerLinks({ paymentLink, chatLink }: { paymentLink?: string | null; chatLink?: string | null }) {
  if (!paymentLink && !chatLink) return null;

  return (
    <div className="flex flex-wrap justify-center gap-2">
      {paymentLink && (
        <a
          href={paymentLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-card/50 border border-border text-xs text-muted-foreground hover:text-foreground hover:border-border/80 transition-colors"
        >
          <CreditCard className="w-3.5 h-3.5" />
          תשלום
        </a>
      )}
      {chatLink && (
        <a
          href={chatLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-card/50 border border-border text-xs text-muted-foreground hover:text-foreground hover:border-border/80 transition-colors"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          צ׳אט קבוצתי
        </a>
      )}
    </div>
  );
}

export default WaitingRoom;
